import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import DescriptionList from '../components/shared/DescriptionList'
import { TransactionOptions } from '../components/aircraft/TransactionOptions'
import AircraftNotes from '../components/notes/AircraftNotes'
import AircraftCompanies from '../components/aircraft/AircraftCompanies'
import OverrideDataModal from '../components/overrides/OverrideDataModal'
import { AircraftTransactionsService } from '../services/aircraft-transactions.service'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { TransactionOverridesNewService } from '../services/transaction-overrides-new.service'
import { AircraftTransaction } from '../types/database'
import { Aircraft } from '../types/intellijetnet'
import { capitalizeMakeName, formatModelName, formatPrice, formatDate } from '../utils/formatters'

export default function AircraftTransactionDetail() {
  const { modelId, transactionId } = useParams<{ modelId: string; transactionId: string }>()
  const navigate = useNavigate()
  const [transaction, setTransaction] = useState<AircraftTransaction | null>(null)
  const [effectiveTransaction, setEffectiveTransaction] = useState<AircraftTransaction | null>(null)
  const [aircraft, setAircraft] = useState<Aircraft | null>(null)
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showOverrideModal, setShowOverrideModal] = useState(false)

  useEffect(() => {
    if (modelId && transactionId) {
      fetchData()
    }
    // Always scroll to top when component mounts
    window.scrollTo(0, 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, transactionId])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const numericModelId = parseInt(modelId!, 10)

      // Fetch transaction data
      const transactionData = await AircraftTransactionsService.getById(transactionId!)
      if (!transactionData) {
        throw new Error('Transaction not found')
      }
      setTransaction(transactionData)

      // Fetch transaction overrides and create effective transaction
      try {
        const transactionOverride = await TransactionOverridesNewService.getByTransactionId(transactionId!)
        if (transactionOverride) {
          // Merge override data with transaction data
          const effective = TransactionOverridesNewService.getEffectiveTransactionData(transactionData, transactionOverride)
          setEffectiveTransaction(effective)
        } else {
          // No overrides, use original transaction data
          setEffectiveTransaction(transactionData)
        }
      } catch (overrideErr) {
        // Error loading transaction overrides, using original data
        setEffectiveTransaction(transactionData)
      }

      // Fetch aircraft data
      const aircraftData = await AircraftService.getById(transactionData.aircraft_id)
      if (!aircraftData) {
        throw new Error('Aircraft not found')
      }
      setAircraft(aircraftData)

      // Fetch model info
      const modelData = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!modelData) {
        throw new Error('Model information not found')
      }
      setModelInfo(modelData)

    } catch (err: any) {
      setError(err.message || 'Failed to load transaction data')
    } finally {
      setLoading(false)
    }
  }

  const handleTransactionUpdate = async (updatedTransaction: AircraftTransaction) => {
    setTransaction(updatedTransaction)

    // Also update the effective transaction with any overrides
    try {
      const transactionOverride = await TransactionOverridesNewService.getByTransactionId(updatedTransaction.transaction_id)
      if (transactionOverride) {
        const effective = TransactionOverridesNewService.getEffectiveTransactionData(updatedTransaction, transactionOverride)
        setEffectiveTransaction(effective)
      } else {
        setEffectiveTransaction(updatedTransaction)
      }
    } catch (err) {
      setEffectiveTransaction(updatedTransaction)
    }
  }

  const handleOverrideSave = async () => {
    // Refresh the transaction data and overrides to get updated data
    if (transactionId) {
      try {
        // Re-fetch the transaction to get the updated data (since TransactionOverridesService updates the aircraft_transactions table directly)
        const updatedTransaction = await AircraftTransactionsService.getById(transactionId)
        if (updatedTransaction) {
          setTransaction(updatedTransaction)

          // Also check for any separate transaction overrides and merge them
          const transactionOverride = await TransactionOverridesNewService.getByTransactionId(transactionId)
          if (transactionOverride) {
            // Merge override data with transaction data
            const effective = TransactionOverridesNewService.getEffectiveTransactionData(updatedTransaction, transactionOverride)
            setEffectiveTransaction(effective)
          } else {
            // No separate overrides, use the updated transaction data directly
            setEffectiveTransaction(updatedTransaction)
          }
        }
      } catch (err) {
        // Error refreshing transaction data
      }
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-950"></div>
        </div>
      </Layout>
    )
  }

  if (error || !transaction || !effectiveTransaction || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">{error || 'Transaction not found'}</div>
            <button
              onClick={() => navigate(`/market/${modelId}`)}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Market
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
          <div className="w-full max-w-full">
            <div className="mb-6">
              <button
                onClick={() => navigate(`/market/${modelId}`)}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center"
              >
                ← Back to Market
              </button>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <h1 className="text-3xl font-bold text-purple-950">
                    {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)} - {transaction.sernbr}
                  </h1>
                  <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    Sold Transaction
                  </div>
                </div>
                <button
                  onClick={() => setShowOverrideModal(true)}
                  className="bg-purple-950 text-white hover:bg-purple-900 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150"
                >
                  Override Data
                </button>
              </div>
            </div>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Transaction Snapshot:</strong> This is a historical snapshot of the aircraft data at the time of sale. 
                Changes made here will only affect this transaction record and will not impact the original aircraft data.
              </p>
            </div>

          {/* Full-width information sections */}
          <div className="space-y-8">
            {/* Notes */}
            <AircraftNotes
              aircraftId={transaction.aircraft_id}
              modelId={transaction.model_id}
            />

            <div className="bg-white shadow-sm rounded-lg p-6">
              <DescriptionList columns={2} sections={[
                    {
                      title: 'Transaction Information',
                      subtitle: 'Sale details and pricing',
                      items: [
                        { label: 'Transaction ID', value: transaction.transaction_id },
                        { label: 'Transaction Date', value: transaction.transaction_date, formatter: formatDate },
                        { label: 'Sale Price (Internal)', value: effectiveTransaction.sale_price, formatter: formatPrice },
                        { label: 'Sale Price (External)', value: effectiveTransaction.external_sale_price, formatter: formatPrice },
                        { label: 'Transaction Type', value: transaction.transaction_type },
                      ]
                    },
                    {
                      title: 'Historical Pricing (At Time of Sale)',
                      subtitle: 'Asking and take prices when sold',
                      items: [
                        { label: 'Asking Price (Internal)', value: effectiveTransaction.asking_price, formatter: formatPrice },
                        { label: 'Take Price (Internal)', value: effectiveTransaction.take_price, formatter: formatPrice },
                        { label: 'Asking Price (External)', value: effectiveTransaction.external_asking_price, formatter: formatPrice },
                        { label: 'Take Price (External)', value: effectiveTransaction.external_take_price, formatter: formatPrice },
                      ]
                    },
                    {
                      title: 'Aircraft Specifications',
                      subtitle: 'Technical details at time of sale',
                      items: [
                        { label: 'Serial Number', value: transaction.sernbr },
                        { label: 'Registration', value: transaction.regnbr },
                        { label: 'Year Delivered', value: transaction.yeardlv },
                        { label: 'AFTT', value: effectiveTransaction.aftt },
                        { label: 'TBO', value: effectiveTransaction.tbo },
                        { label: 'Landings', value: effectiveTransaction.landings },
                        { label: '# PAX', value: effectiveTransaction.pax },
                      ]
                    },
                    {
                      title: 'Location Information',
                      subtitle: 'Base location at time of sale',
                      items: [
                        { label: 'Base Airport', value: effectiveTransaction.base_airport },
                        { label: 'Base City', value: effectiveTransaction.base_city },
                        { label: 'Base State', value: effectiveTransaction.base_state },
                        { label: 'Base Country', value: effectiveTransaction.base_country },
                      ]
                    },
                    {
                      title: 'Status and Maintenance',
                      subtitle: 'Aircraft condition at time of sale',
                      items: [
                        { label: 'Damage History', value: effectiveTransaction.damage_history },
                        { label: 'EMP', value: effectiveTransaction.emp },
                        { label: 'Status', value: effectiveTransaction.status },
                        { label: 'Maintenance Program', value: effectiveTransaction.maintenance_program },
                      ]
                    },
                    {
                      title: 'Custom Data',
                      subtitle: 'Custom fields and notes',
                      items: [
                        { label: 'Custom Column 1', value: effectiveTransaction.custom_1_value },
                        { label: 'Custom Column 2', value: effectiveTransaction.custom_2_value },
                        { label: 'Custom Column 3', value: effectiveTransaction.custom_3_value },
                        { label: 'Transaction Notes', value: effectiveTransaction.transaction_notes },
                      ]
                    },
                    {
                      title: 'System Information',
                      subtitle: 'Database and audit information',
                      items: [
                        { label: 'Created At', value: transaction.created_at, formatter: formatDate },
                        { label: 'Updated At', value: transaction.updated_at, formatter: formatDate },
                        { label: 'Created By', value: transaction.created_by },
                        { label: 'Updated By', value: transaction.updated_by },
                      ]
                    },
              ]} />
            </div>
          </div>

          {/* Editable sections at the bottom */}
          <div className="mt-8 space-y-8">
            <TransactionOptions
              transaction={transaction}
              modelId={transaction.model_id}
              onUpdate={handleTransactionUpdate}
              readonly={false}
            />

            <AircraftCompanies
              aircraftId={transaction.aircraft_id}
              override={{
                id: '',
                aircraft_id: transaction.aircraft_id,
                owner: transaction.owner,
                broker: transaction.broker,
                owner_custom: transaction.owner_custom,
                broker_custom: transaction.broker_custom,
                created_at: transaction.created_at,
                updated_at: transaction.updated_at
              }}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Override Data Modal */}
      {aircraft && (
        <OverrideDataModal
          isOpen={showOverrideModal}
          onClose={() => setShowOverrideModal(false)}
          aircraft={aircraft}
          onSave={handleOverrideSave}
          containerType="sold"
          transaction={transaction}
        />
      )}
    </Layout>
  )
}
